import { type KeyboardEvent, type ReactNode, useId, useRef } from 'react'
import { formatNumber } from '@/utils/format'
import { cx } from '../cx'
import styles from './SegmentedControl.module.css'

export interface SegmentedControlOption<V extends string> {
  value: V
  label: string
  preview?: ReactNode
  count?: number
}

/**
 * Two to four options; a preference that applies immediately. Beyond four, or
 * inside a submitted form, use RadioGroup.
 */
export function SegmentedControl<V extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: V
  options: SegmentedControlOption<V>[]
  onChange: (value: V) => void
  className?: string
}) {
  const inputs = useRef<Array<HTMLInputElement | null>>([])
  const name = useId()
  // With no option selected, the first one takes the tab stop.
  const hasSelection = options.some((option) => option.value === value)

  function selectFromKeyboard(
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) {
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % options.length
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + options.length) % options.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = options.length - 1
    }

    if (nextIndex == null) return
    event.preventDefault()
    const next = options[nextIndex]
    onChange(next.value)
    inputs.current[nextIndex]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(styles.track, className)}
      data-options={options.length}
    >
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <label
            key={option.value}
            className={styles.option}
            data-preview={option.preview != null ? '' : undefined}
          >
            <input
              ref={(element) => {
                inputs.current[index] = element
              }}
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              tabIndex={selected || (!hasSelection && index === 0) ? 0 : -1}
              className={styles.input}
              onChange={() => onChange(option.value)}
              onKeyDown={(event) => selectFromKeyboard(event, index)}
            />
            {option.preview != null && (
              <span className={styles.preview} aria-hidden="true">
                {option.preview}
              </span>
            )}
            <span className={styles.label}>{option.label}</span>
            {option.count != null && (
              <>
                {/* Whitespace-only text is not rendered in the flex row, but it
                    keeps the accessible name "Threads 5", not "Threads5". */}{' '}
                <span className={styles.count}>
                  {formatNumber(option.count)}
                </span>
              </>
            )}
          </label>
        )
      })}
    </div>
  )
}
